/**
 * Agent Service
 *
 * Type-safe agent management service using @monorepo/shared schemas
 */

import {
  type AgentQuery,
  type AgentResponse,
  AgentResponseSchema,
  type CreateAgent,
  CreateAgentSchema,
  type PaginatedAgentsResponse,
  PaginatedAgentsResponseSchema,
  type UpdateAgent,
  UpdateAgentSchema,
} from '@monorepo/shared/schemas/agents'
import type { EntityStatus } from '@monorepo/shared/schemas/common'
import { del, get, post, put } from '@/lib/api-client'

export const agentService = {
  /**
   * List all agents (filtered by query)
   */
  async getAgents(query?: AgentQuery): Promise<PaginatedAgentsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.has_external_id !== undefined)
      queryParams.append('has_external_id', query.has_external_id.toString())
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/agents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedAgentsResponseSchema)
  },

  /**
   * Get agent by ID (includes actions)
   */
  async getAgent(id: string): Promise<AgentResponse> {
    return get(`/agents/${id}`, AgentResponseSchema)
  },

  /**
   * Create new agent
   */
  async createAgent(data: CreateAgent): Promise<AgentResponse> {
    const validated = CreateAgentSchema.parse(data)

    return post('/agents', validated, AgentResponseSchema)
  },

  /**
   * Update agent
   */
  async updateAgent(id: string, data: UpdateAgent): Promise<AgentResponse> {
    const validated = UpdateAgentSchema.parse(data)

    return put(`/agents/${id}`, validated, AgentResponseSchema)
  },

  /**
   * Delete agent (soft delete - archives it)
   */
  async deleteAgent(id: string): Promise<void> {
    await del(`/agents/${id}`)
  },

  /**
   * Update agent status
   */
  async updateStatus(id: string, status: EntityStatus): Promise<AgentResponse> {
    const validated = UpdateAgentSchema.parse({ status })

    return put(`/agents/${id}`, validated, AgentResponseSchema)
  },
}
